

import { useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ThemeToggle } from "@/components/theme-toggle";
import { 
  Phone, 
  MapPin, 
  Clock, 
  ChevronDown, 
  Menu as MenuIcon, 
  X,
  Star,
  Users,
  Utensils,
  PartyPopper,
  Instagram,
  Facebook
} from "lucide-react";

import heroImage from "@assets/stock_images/delicious_grilled_sh_22170f72.jpg";
import fruitPlatter from "@assets/stock_images/colorful_tropical_fr_e29fb190.jpg";
import chickenWings from "@assets/stock_images/glazed_chicken_wings_6899a6d9.jpg";
import cateringBuffet from "@assets/stock_images/elegant_catering_buf_6de23805.jpg";
import chefImage from "@assets/stock_images/professional_chef_pr_10d8c235.jpg";
import steakImage from "@assets/stock_images/grilled_steak_with_v_17c6f4e0.jpg";
import pastaImage from "@assets/stock_images/fresh_seafood_pasta__d567a83d.jpg";
import weddingEvent from "@assets/stock_images/elegant_wedding_rece_77680cb4.jpg";
import dessertImage from "@assets/stock_images/beautiful_dessert_ch_0ae39da2.jpg";
import miamiSkyline from "@assets/stock_images/miami_beach_skyline__cb318431.jpg";
import shrimpPlatter from "@assets/img01.jpeg";
import fruitDisplay from "@assets/img02.jpeg";

const menuItems = [
  { id: 1, name: "Garlic Butter Shrimp", description: "Succulent shrimp sautéed in rich garlic herb butter", category: "Seafood", image: shrimpPlatter },
  { id: 2, name: "Tropical Fruit Platter", description: "Fresh seasonal fruits beautifully arranged with orchid garnish", category: "Fresh & Light", image: fruitDisplay },
  { id: 3, name: "Honey Glazed Wings", description: "Crispy wings tossed in our signature sweet & spicy glaze", category: "Appetizers", image: chickenWings },
  { id: 4, name: "Prime Ribeye Steak", description: "USDA Prime beef grilled to perfection with seasonal vegetables", category: "Main Course", image: steakImage },
  { id: 5, name: "Seafood Pasta", description: "Fresh shrimp and scallops in creamy garlic sauce over linguine", category: "Pasta", image: pastaImage },
  { id: 6, name: "Chocolate Dream Cake", description: "Rich chocolate layered cake with fresh berries", category: "Desserts", image: dessertImage },
];

const cateringPackages = [
  {
    name: "Intimate Gatherings",
    price: "$500",
    description: "Perfect for small events up to 25 guests",
    features: ["Choice of 3 appetizers", "2 main course options", "Fresh fruit display", "Setup & cleanup included"],
    icon: Users,
  },
  {
    name: "Celebration Package",
    price: "$1,200",
    description: "Ideal for parties of 50-75 guests",
    features: ["Full appetizer spread", "4 main course selections", "Dessert station", "Dedicated service staff", "Custom menu options"],
    icon: PartyPopper,
    featured: true,
  },
  {
    name: "Grand Events",
    price: "$2,500+",
    description: "Full-service catering for 100+ guests",
    features: ["Complete menu customization", "Premium beverage service", "Chef on-site", "Event coordination", "Décor consultation"],
    icon: Utensils,
  },
];

const testimonials = [
  { name: "Maria Rodriguez", event: "Wedding Reception", quote: "Taste of Flava made our special day unforgettable. The food was incredible!", rating: 5 },
  { name: "James Thompson", event: "Corporate Event", quote: "Professional, delicious, and beautifully presented. Our clients were impressed!", rating: 5 },
  { name: "Tanisha Williams", event: "Birthday Party", quote: "The best soul food in Miami! Everyone asked for the caterer's number.", rating: 5 },
];

export default function Home() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { scrollY } = useScroll();
  const heroOpacity = useTransform(scrollY, [0, 400], [1, 0]);
  const heroScale = useTransform(scrollY, [0, 400], [1, 1.1]);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* Navigation */}
      <motion.header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? "bg-background/95 backdrop-blur-md shadow-lg" : "bg-transparent"
        }`}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 md:h-20">
            <motion.div 
              className="flex-shrink-0"
              whileHover={{ scale: 1.02 }}
            >
              <h1 className={`font-serif text-xl md:text-2xl font-bold tracking-tight ${
                isScrolled ? "text-foreground" : "text-white"
              }`} data-testid="logo-main">
                Taste of <span className="text-primary">Flava</span>
              </h1>
            </motion.div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              {["About", "Menu", "Catering", "Contact"].map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className={`text-sm font-medium transition-colors ${
                    isScrolled 
                      ? "text-muted-foreground hover:text-foreground" 
                      : "text-white/80 hover:text-white"
                  }`}
                  data-testid={`nav-${item.toLowerCase()}`}
                >
                  {item}
                </button>
              ))}
              <Button 
                onClick={() => scrollToSection("contact")}
                data-testid="nav-order-now"
              >
                <Phone className="w-4 h-4 mr-2" />
                Order Now
              </Button>
              <ThemeToggle />
            </div>

            {/* Mobile Menu Button */}
            <Button
              variant="ghost"
              size="icon"
              className={`md:hidden ${isScrolled ? "" : "text-white"}`}
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              data-testid="button-mobile-menu"
            >
              {isMenuOpen ? <X /> : <MenuIcon />}
            </Button>
          </div>
        </nav>

        {/* Mobile Menu */}
        <motion.div
          className={`md:hidden bg-background border-t ${isMenuOpen ? "block" : "hidden"}`}
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: isMenuOpen ? 1 : 0, height: isMenuOpen ? "auto" : 0 }}
        >
          <div className="px-4 py-4 space-y-3">
            {["About", "Menu", "Catering", "Contact"].map((item) => (
              <button
                key={item}
                onClick={() => scrollToSection(item.toLowerCase())}
                className="block w-full text-left py-2 text-foreground hover:text-primary transition-colors"
                data-testid={`mobile-nav-${item.toLowerCase()}`}
              >
                {item}
              </button>
            ))}
            <Button 
              className="w-full mt-4"
              onClick={() => scrollToSection("contact")}
              data-testid="mobile-order-now"
            >
              <Phone className="w-4 h-4 mr-2" />
              Order Now
            </Button>
            <div className="flex justify-center pt-2">
              <ThemeToggle />
            </div>
          </div>
        </motion.div>
      </motion.header>

      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <motion.div 
          className="absolute inset-0 z-0"
          style={{ opacity: heroOpacity, scale: heroScale }}
        >
          <img
            src={heroImage}
            alt="Delicious grilled steak with vegetables"
            className="w-full h-full object-cover"
          />
        </motion.div>
        
        <div className="absolute inset-0 bg-black/40 z-0" />
        
        <div className="relative z-10 text-center px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto">
          <motion.h2 
            className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            Miami's Premier Catering
          </motion.h2>
          <motion.h1 
            className="text-5xl sm:text-6xl md:text-7xl font-bold text-white mb-6 font-serif drop-shadow-lg"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            Taste of <span className="text-primary">Flava</span>
          </motion.h1>
          <motion.p 
            className="text-lg sm:text-xl text-white/90 mb-8 max-w-2xl mx-auto drop-shadow-md"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            Bringing the vibrant flavors of the Caribbean and soul food tradition to your special events in Miami.
          </motion.p>
          <motion.div 
            className="flex flex-col sm:flex-row justify-center gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <Button 
              size="lg" 
              className="bg-primary hover:bg-primary/90 text-white font-medium px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              onClick={() => scrollToSection('menu')}
            >
              View Our Menu
            </Button>
            <Button 
              variant="outline" 
              size="lg" 
              className="border-2 border-white text-white hover:bg-white/10 font-medium px-8 py-6 text-lg shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
              onClick={() => scrollToSection('contact')}
            >
              Contact Us
            </Button>
          </motion.div>
          <motion.div 
            className="mt-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            whileHover={{ scale: 1.03 }}
          >
            <span className="inline-flex items-center px-6 py-3 text-white/95 text-base sm:text-lg font-medium bg-black/50 backdrop-blur-md border border-white/20 rounded-full hover:bg-black/60 transition-all duration-300 shadow-lg">
              <span className="relative flex h-2 w-2 mr-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary/80 opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
              </span>
              Catering Packages Starting at <span className="text-primary font-bold ml-1">$500</span>
            </span>
          </motion.div>
        </div>
        
        <motion.div 
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
        >
          <ChevronDown className="w-10 h-10 text-white/90 hover:text-white cursor-pointer transition-colors" />
        </motion.div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 md:py-28 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="relative rounded-md overflow-hidden">
                <img
                  src={chefImage}
                  alt="Our passionate chef preparing dishes"
                  className="w-full h-[400px] md:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-primary text-primary-foreground p-6 rounded-md shadow-lg">
                <p className="font-serif text-3xl font-bold">15+</p>
                <p className="text-sm">Years of Excellence</p>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-primary font-medium text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2 mb-6 text-foreground" data-testid="text-about-title">
                A Passion for Flavor, <br />Rooted in Miami
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p data-testid="text-about-paragraph-1">
                  Born from a deep love for Caribbean cuisine and Southern soul food traditions, 
                  Taste of Flava has been serving Miami's most memorable events since 2009.
                </p>
                <p data-testid="text-about-paragraph-2">
                  Our kitchen blends the bold spices of the islands with the comfort of home-cooked 
                  Southern classics, creating unique dishes that tell a story with every bite.
                </p>
                <p data-testid="text-about-paragraph-3">
                  From intimate family gatherings to grand celebrations, we bring the same passion 
                  and attention to detail that has made us Miami's trusted catering partner.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-6 mt-8 pt-8 border-t border-border">
                <div className="text-center">
                  <p className="font-serif text-2xl font-bold text-primary" data-testid="stat-events">500+</p>
                  <p className="text-sm text-muted-foreground" data-testid="label-stat-events">Events Catered</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl font-bold text-primary" data-testid="stat-guests">50K+</p>
                  <p className="text-sm text-muted-foreground" data-testid="label-stat-guests">Happy Guests</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-2xl font-bold text-primary" data-testid="stat-rating">5.0</p>
                  <p className="text-sm text-muted-foreground flex items-center justify-center gap-1" data-testid="label-stat-rating">
                    <Star className="w-3 h-3 fill-primary text-primary" />
                    Rating
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Menu Gallery Section */}
      <section id="menu" className="py-20 md:py-28 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Our Menu</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2 mb-4 text-foreground" data-testid="text-menu-title">
              Signature Dishes
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto" data-testid="text-menu-description">
              Each dish is crafted with love, featuring the freshest ingredients and 
              authentic flavors that will transport your guests to the Caribbean
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="overflow-hidden group hover-elevate" data-testid={`menu-item-${item.id}`}>
                  <div className="relative h-56 overflow-hidden">
                    <img
                      src={item.image}
                      alt={item.name}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-4 left-4">
                      <span className="px-3 py-1 bg-primary/90 text-primary-foreground text-xs font-medium rounded-full" data-testid={`badge-menu-category-${item.id}`}>
                        {item.category}
                      </span>
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-serif text-xl font-semibold text-foreground mb-2" data-testid={`text-menu-item-name-${item.id}`}>
                      {item.name}
                    </h3>
                    <p className="text-muted-foreground text-sm leading-relaxed" data-testid={`text-menu-item-desc-${item.id}`}>
                      {item.description}
                    </p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <p className="text-muted-foreground mb-4">
              This is just a taste of what we offer. Contact us for our full menu and custom options.
            </p>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => scrollToSection("contact")}
              data-testid="menu-request-full"
            >
              Request Full Menu
            </Button>
          </motion.div>
        </div>
      </section>

      {/* Catering Services Section */}
      <section id="catering" className="py-20 md:py-28 bg-background relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <img src={cateringBuffet} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Services</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2 mb-4 text-foreground" data-testid="text-catering-title">
              Catering Packages
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto" data-testid="text-catering-description">
              From intimate dinners to grand celebrations, we have the perfect package 
              for your special occasion
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {cateringPackages.map((pkg, index) => (
              <motion.div
                key={pkg.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.15 }}
              >
                <Card 
                  className={`relative h-full p-6 ${
                    pkg.featured 
                      ? "border-primary bg-primary/5" 
                      : ""
                  }`}
                  data-testid={`catering-package-${index}`}
                >
                  {pkg.featured && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span className="px-4 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                        Most Popular
                      </span>
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <div className={`inline-flex items-center justify-center w-14 h-14 rounded-full mb-4 ${
                      pkg.featured ? "bg-primary text-primary-foreground" : "bg-secondary text-secondary-foreground"
                    }`}>
                      <pkg.icon className="w-6 h-6" />
                    </div>
                    <h3 className="font-serif text-xl font-bold text-foreground" data-testid={`text-package-name-${index}`}>{pkg.name}</h3>
                    <p className="text-muted-foreground text-sm mt-1" data-testid={`text-package-desc-${index}`}>{pkg.description}</p>
                    <p className="font-serif text-3xl font-bold text-primary mt-4" data-testid={`text-package-price-${index}`}>{pkg.price}</p>
                  </div>
                  <ul className="space-y-3 mb-6">
                    {pkg.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-2 text-sm text-muted-foreground" data-testid={`text-package-feature-${index}-${i}`}>
                        <div className="w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <Star className="w-3 h-3 text-primary" />
                        </div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                  <Button 
                    className="w-full"
                    variant={pkg.featured ? "default" : "outline"}
                    onClick={() => scrollToSection("contact")}
                    data-testid={`catering-book-${index}`}
                  >
                    Book This Package
                  </Button>
                </Card>
              </motion.div>
            ))}
          </div>

          {/* Event Types */}
          <motion.div
            className="mt-20"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h3 className="font-serif text-2xl font-bold text-center mb-8 text-foreground">
              We Cater All Types of Events
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { name: "Weddings", image: weddingEvent },
                { name: "Corporate Events", image: cateringBuffet },
                { name: "Private Parties", image: fruitPlatter },
                { name: "Family Reunions", image: chefImage },
              ].map((event, index) => (
                <motion.div
                  key={event.name}
                  className="relative h-40 rounded-md overflow-hidden group"
                  whileHover={{ y: -4 }}
                  data-testid={`event-type-${index}`}
                >
                  <img
                    src={event.image}
                    alt={event.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                  <div className="absolute bottom-4 left-4 right-4">
                    <p className="text-white font-semibold" data-testid={`text-event-name-${index}`}>{event.name}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 md:py-28 bg-card">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Testimonials</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2 mb-4 text-foreground" data-testid="text-testimonials-title">
              What Our Clients Say
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="p-6 h-full" data-testid={`testimonial-${index}`}>
                  <div className="flex gap-1 mb-4" data-testid={`rating-stars-${index}`}>
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-foreground italic mb-6 leading-relaxed" data-testid={`text-testimonial-quote-${index}`}>
                    "{testimonial.quote}"
                  </p>
                  <div className="mt-auto">
                    <p className="font-semibold text-foreground" data-testid={`text-testimonial-name-${index}`}>{testimonial.name}</p>
                    <p className="text-sm text-muted-foreground" data-testid={`text-testimonial-event-${index}`}>{testimonial.event}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 md:py-28 bg-background relative">
        <div className="absolute inset-0 opacity-10">
          <img src={miamiSkyline} alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium text-sm uppercase tracking-wider">Get In Touch</span>
            <h2 className="font-serif text-3xl md:text-4xl font-bold mt-2 mb-4 text-foreground" data-testid="text-contact-title">
              Let's Create Something Special
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto" data-testid="text-contact-description">
              Ready to bring the flavor to your next event? Contact us today for a 
              free consultation and custom quote.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 lg:gap-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8">
                <h3 className="font-serif text-xl font-bold mb-6 text-foreground" data-testid="heading-contact-info">Contact Information</h3>
                
                <div className="space-y-6">
                  <a 
                    href="tel:305-766-9561" 
                    className="flex items-start gap-4 group"
                    data-testid="contact-phone"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary/20 transition-colors">
                      <Phone className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Phone</p>
                      <p className="text-primary text-lg font-medium" data-testid="text-phone-number">305-766-9561</p>
                    </div>
                  </a>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Location</p>
                      <p className="text-muted-foreground" data-testid="text-location">Serving all of Miami-Dade County</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-semibold text-foreground">Hours</p>
                      <p className="text-muted-foreground" data-testid="text-hours-weekday">Mon - Sat: 9AM - 8PM</p>
                      <p className="text-muted-foreground" data-testid="text-hours-weekend">Sun: 10AM - 6PM</p>
                    </div>
                  </div>
                </div>

                <div className="mt-8 pt-6 border-t border-border">
                  <p className="font-semibold text-foreground mb-4" data-testid="text-follow-us">Follow Us</p>
                  <div className="flex gap-3">
                    <a 
                      href="#" 
                      className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                      data-testid="link-social-instagram"
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a 
                      href="#" 
                      className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-primary hover:text-primary-foreground transition-colors"
                      data-testid="link-social-facebook"
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </div>
                </div>
              </Card>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-8">
                <h3 className="font-serif text-xl font-bold mb-6 text-foreground" data-testid="heading-send-message">Send Us a Message</h3>
                <form className="space-y-4" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Name</label>
                      <input
                        type="text"
                        placeholder="Your name"
                        className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="input-name"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">Phone</label>
                      <input
                        type="tel"
                        placeholder="Your phone"
                        className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email</label>
                    <input
                      type="email"
                      placeholder="Your email"
                      className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      data-testid="input-email"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Event Type</label>
                    <select
                      className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/50"
                      data-testid="select-event-type"
                    >
                      <option value="">Select event type</option>
                      <option value="wedding">Wedding</option>
                      <option value="corporate">Corporate Event</option>
                      <option value="party">Private Party</option>
                      <option value="reunion">Family Reunion</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Message</label>
                    <textarea
                      rows={4}
                      placeholder="Tell us about your event..."
                      className="w-full px-4 py-3 rounded-md border border-input bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                      data-testid="textarea-message"
                    />
                  </div>
                  <Button size="lg" className="w-full" data-testid="button-submit-contact">
                    Send Message
                  </Button>
                </form>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-foreground text-background py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div>
              <h2 className="font-serif text-2xl font-bold" data-testid="text-footer-logo">
                Taste of <span className="text-primary">Flava</span>
              </h2>
              <p className="text-background/70 mt-2" data-testid="text-footer-tagline">Miami's Premier Catering Service</p>
            </div>
            <div className="text-center">
              <p className="text-background/70" data-testid="text-footer-serving">
                Serving Miami-Dade County with love since 2009
              </p>
            </div>
            <div className="text-right">
              <a 
                href="tel:305-766-9561" 
                className="text-primary text-xl font-bold hover:underline"
                data-testid="footer-phone"
              >
                305-766-9561
              </a>
              <p className="text-background/70 mt-1" data-testid="text-footer-cta">Call for a free consultation</p>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-background/20 text-center text-background/50 text-sm">
            <p data-testid="text-copyright">&copy; {new Date().getFullYear()} Taste of Flava. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
